import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';

const ESCALATION = {
  peace:                { level:1, color:'#1a1a2e', label:'Peace',                desc:'No significant conflict indicators. Normal diplomatic and economic relations.' },
  economic_competition: { level:2, color:'#2d6a4f', label:'Economic Competition', desc:'Trade disputes, sanctions, economic coercion, resource competition.' },
  clandestine:          { level:3, color:'#52b788', label:'Clandestine',          desc:'Sponsor AND operation concealed but probable. Intelligence ops, covert sabotage.' },
  covert:               { level:4, color:'#ffcc33', label:'Covert',               desc:'Sponsor concealed but probable; operation known. Proxy support, deniable cyber.' },
  overt:                { level:5, color:'#ff9933', label:'Overt',                desc:'Sponsor AND operation known. Declared proxy support, acknowledged advisors.' },
  limited_conventional: { level:6, color:'#e63946', label:'Limited Conventional', desc:'Localized border skirmishes, naval blockades, local proxy wars.' },
  lsco:                 { level:7, color:'#c1121f', label:'LSCO',                 desc:'Nations commit major regular forces. Attritional or maneuver conventional warfare.' },
  global_strategic:     { level:8, color:'#7b2d8b', label:'Global Strategic War', desc:'Multi-national coalitions across Air/Land/Sea/Space.' },
  nuclear_chemical:     { level:9, color:'#3d0000', label:'Nuclear/Chemical War', desc:'WMD use with disregard for civilian casualties.' },
};

export const DOMAINS = {
  D1: { label:'Air',         icon:'✈️',  color:'#64b5f6', geo:true,  desc:'Airspace incidents, air defense zones, flight restrictions' },
  D2: { label:'Land',        icon:'🏔️', color:'#81c784', geo:true,  desc:'Ground operations, border incidents, land force movements' },
  D3: { label:'Maritime',    icon:'⚓',  color:'#00bcd4', geo:true,  desc:'Shipping lanes, chokepoints, naval operations, EEZ disputes' },
  D4: { label:'Space',       icon:'🛰️', color:'#ce93d8', geo:false, desc:'Orbital assets, satellite interference, space domain awareness' },
  D5: { label:'Cyberspace',  icon:'🔐', color:'#ff8a65', geo:false, desc:'Network intrusions, critical infrastructure attacks, cyber campaigns' },
  D6: { label:'Information', icon:'📡', color:'#fff176', geo:true,  desc:'Influence operations, disinformation, PSYOP, media manipulation' },
  D7: { label:'Cognitive',   icon:'🧠', color:'#f48fb1', geo:false, desc:'Narrative shaping, population perception, adversarial cognition' },
};

// Full AOR zoom — captures entire geographic area
const REGION_ZOOM = { 1:2.4, 2:2.8, 3:1.8, 4:2.3, 5:2.4, 6:3.2 };

const addSourceSafe = (map, id, data) => { if (!map.getSource(id)) map.addSource(id, { type:'geojson', data }); };
const addLayerSafe  = (map, def)      => { if (!map.getLayer(def.id)) map.addLayer(def); };

const SHIPPING_LANES = {
  type:'FeatureCollection', features:[
    { type:'Feature', properties:{ name:'North Atlantic' },        geometry:{ type:'LineString', coordinates:[[-5,36],[-10,40],[-20,45],[-35,48],[-50,47],[-60,44],[-70,40],[-75,35]] }},
    { type:'Feature', properties:{ name:'Trans-Pacific (North)' }, geometry:{ type:'LineString', coordinates:[[121,31],[140,35],[155,40],[170,45],[180,47],[-170,48],[-155,45],[-140,40],[-125,35],[-118,34]] }},
    { type:'Feature', properties:{ name:'Trans-Pacific (South)' }, geometry:{ type:'LineString', coordinates:[[121,22],[135,20],[150,15],[165,10],[180,5],[-175,0],[-160,-5],[-145,-10],[-120,-15],[-80,-12]] }},
    { type:'Feature', properties:{ name:'Indian Ocean' },          geometry:{ type:'LineString', coordinates:[[32,28],[43,13],[50,10],[55,5],[60,0],[72,-5],[80,-10],[90,-5],[100,2],[104,1],[110,0],[120,-5]] }},
    { type:'Feature', properties:{ name:'Mediterranean–Suez' },   geometry:{ type:'LineString', coordinates:[[-5,36],[5,37],[15,37],[25,33],[32,30],[33,28],[32,27],[36,22],[43,13]] }},
    { type:'Feature', properties:{ name:'Cape of Good Hope' },     geometry:{ type:'LineString', coordinates:[[5,37],[0,15],[-5,0],[-5,-20],[10,-35],[18,-34],[25,-30],[32,-28]] }},
    { type:'Feature', properties:{ name:'South Atlantic' },        geometry:{ type:'LineString', coordinates:[[-43,-23],[-35,-15],[-20,-5],[-10,0],[0,5],[5,15],[0,30],[-5,36]] }},
    { type:'Feature', properties:{ name:'Malacca Strait' },        geometry:{ type:'LineString', coordinates:[[100,2],[101,2],[102,2],[103,1],[104,1],[105,0],[106,0]] }},
    { type:'Feature', properties:{ name:'Persian Gulf' },          geometry:{ type:'LineString', coordinates:[[50,26],[54,24],[57,23],[58,22],[58,18],[55,13],[50,10]] }},
    { type:'Feature', properties:{ name:'Europe–Asia' },           geometry:{ type:'LineString', coordinates:[[-5,36],[10,37],[30,32],[40,20],[50,10],[60,5],[70,8],[80,10],[90,8],[100,2],[104,1]] }},
  ]
};

const CHOKEPOINTS = {
  type:'FeatureCollection', features:[
    { type:'Feature', properties:{ name:'Suez Canal',        detail:'~12,000 ships/yr' }, geometry:{ type:'Point', coordinates:[32.35,30.0] }},
    { type:'Feature', properties:{ name:'Strait of Hormuz',  detail:'~21M bbl/day' },     geometry:{ type:'Point', coordinates:[56.5,26.5] }},
    { type:'Feature', properties:{ name:'Strait of Malacca', detail:'~100,000 ships/yr' },geometry:{ type:'Point', coordinates:[103.5,1.5] }},
    { type:'Feature', properties:{ name:'Bab-el-Mandeb',     detail:'Red Sea gateway' },  geometry:{ type:'Point', coordinates:[43.5,12.5] }},
    { type:'Feature', properties:{ name:'Cape of Good Hope', detail:'Africa bypass' },    geometry:{ type:'Point', coordinates:[18.5,-34.0] }},
    { type:'Feature', properties:{ name:'Drake Passage',     detail:'S. America bypass' },geometry:{ type:'Point', coordinates:[-67.0,-56.0] }},
    { type:'Feature', properties:{ name:'Danish Straits',    detail:'Baltic gateway' },   geometry:{ type:'Point', coordinates:[12.5,56.0] }},
    { type:'Feature', properties:{ name:'Taiwan Strait',     detail:'W. Pacific route' }, geometry:{ type:'Point', coordinates:[120.5,24.5] }},
    { type:'Feature', properties:{ name:'Panama Canal',      detail:'~14,000 ships/yr' }, geometry:{ type:'Point', coordinates:[-79.5,9.0] }},
    { type:'Feature', properties:{ name:'Gibraltar',         detail:'Med gateway' },      geometry:{ type:'Point', coordinates:[-5.4,36.0] }},
  ]
};

const AIR_INCIDENTS = {
  type:'FeatureCollection', features:[
    { type:'Feature', properties:{ label:'ADIZ incursion', date:'29 May' }, geometry:{ type:'Point', coordinates:[122,25] }},
    { type:'Feature', properties:{ label:'Airspace closure', date:'28 May' }, geometry:{ type:'Point', coordinates:[50,28] }},
    { type:'Feature', properties:{ label:'NFZ active', date:'30 May' }, geometry:{ type:'Point', coordinates:[37,48] }},
  ]
};

const LAND_INCIDENTS = {
  type:'FeatureCollection', features:[
    { type:'Feature', properties:{ label:'Border skirmish', date:'30 May' }, geometry:{ type:'Point', coordinates:[31,50] }},
    { type:'Feature', properties:{ label:'Ground op', date:'29 May' }, geometry:{ type:'Point', coordinates:[44,33] }},
    { type:'Feature', properties:{ label:'Troop movement', date:'28 May' }, geometry:{ type:'Point', coordinates:[100,22] }},
  ]
};

const INFO_OPS = {
  type:'FeatureCollection', features:[
    { type:'Feature', properties:{ label:'Disinformation campaign', date:'30 May' }, geometry:{ type:'Point', coordinates:[25,52] }},
    { type:'Feature', properties:{ label:'State media push', date:'29 May' }, geometry:{ type:'Point', coordinates:[116,40] }},
    { type:'Feature', properties:{ label:'Influence op', date:'28 May' }, geometry:{ type:'Point', coordinates:[51,35] }},
  ]
};

// Collapsible legend component
const Legend = ({ activeDomains }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position:'absolute', bottom:'20px', left:'20px', zIndex:5, minWidth:'180px' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display:'flex', alignItems:'center', gap:'6px',
        backgroundColor:'rgba(15,20,25,0.95)', border:'1px solid rgba(196,165,99,0.3)',
        borderRadius: open ? '8px 8px 0 0' : '8px',
        color:'#c4a563', fontSize:'11px', fontWeight:'bold', letterSpacing:'0.5px',
        cursor:'pointer', padding:'8px 12px', width:'100%'
      }}>
        <span>🎖️ LEGEND</span>
        <span style={{ marginLeft:'auto', opacity:0.6 }}>{open ? '▼' : '▶'}</span>
      </button>
      {open && (
        <div style={{ backgroundColor:'rgba(15,20,25,0.97)', border:'1px solid rgba(196,165,99,0.3)',
          borderTop:'none', borderRadius:'0 0 8px 8px', padding:'10px 12px' }}>
          <div style={{ color:'#555', fontSize:'9px', fontWeight:'bold', letterSpacing:'0.5px', marginBottom:'7px' }}>ESCALATION</div>
          {Object.entries(ESCALATION).filter(([k]) => k !== 'peace').map(([k,v]) => (
            <div key={k} style={{ display:'flex', alignItems:'flex-start', gap:'7px', marginBottom:'5px' }}>
              <div style={{ width:'9px', height:'9px', backgroundColor:v.color, borderRadius:'2px', flexShrink:0, marginTop:'2px' }} />
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                  <span style={{ color:'#444', fontSize:'9px', fontWeight:'bold', minWidth:10 }}>{v.level}.</span>
                  <span style={{ color:'#c4a563', fontWeight:'bold', fontSize:'11px' }}>{v.label}</span>
                </div>
                <div style={{ color:'#555', fontSize:'9px', marginTop:'1px', paddingLeft:14 }}>{v.desc}</div>
              </div>
            </div>
          ))}
          <div style={{ marginTop:'8px', paddingTop:'7px', borderTop:'1px solid rgba(196,165,99,0.1)', color:'#555', fontSize:'9px', lineHeight:1.8 }}>
            No shading = Peace (Level 1)<br/>
            {activeDomains.includes('D3') && <span><span style={{ color:'#00bcd4' }}>── ──</span> Maritime lanes<br/></span>}
            {activeDomains.includes('D3') && <span><span style={{ color:'#ffff00' }}>●</span> Chokepoint<br/></span>}
            {activeDomains.includes('D1') && <span><span style={{ color:'#64b5f6' }}>●</span> Air incident<br/></span>}
            {activeDomains.includes('D2') && <span><span style={{ color:'#81c784' }}>●</span> Land incident<br/></span>}
            {activeDomains.includes('D6') && <span><span style={{ color:'#fff176' }}>●</span> Info op<br/></span>}
          </div>
        </div>
      )}
    </div>
  );
};

const GlobeMap = forwardRef(({ onRegionClick, activeDomains = [], referenceAssessments = {}, referenceEntityId = null }, ref) => {
  const referenceAssessmentsRef = useRef(referenceAssessments);
  const referenceEntityIdRef = useRef(referenceEntityId);
  referenceAssessmentsRef.current = referenceAssessments;
  referenceEntityIdRef.current = referenceEntityId;
  const mapContainer = useRef(null);
  const map          = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useImperativeHandle(ref, () => ({
    flyToRegion: (regionId, lat, lon) => {
      if (!map.current) return;
      map.current.flyTo({
        center: [lon, lat],
        zoom: REGION_ZOOM[regionId] || 3,
        duration: 1400,
        essential: true
      });
      // No region border highlight — we fly instead of drawing
    },
    resetView: () => {
      if (!map.current) return;
      map.current.flyTo({ center:[20,20], zoom:2, duration:1000 });
    }
  }), []);

  // Toggle domain layers
  useEffect(() => {
    if (!map.current || loading) return;
    const domainLayers = {
      D1: ['d1-dots','d1-labels'],
      D2: ['d2-dots','d2-labels'],
      D3: ['d3-shipping','d3-choke-dots','d3-choke-labels','d3-choke-lines'],
      D6: ['d6-dots','d6-labels'],
    };
    Object.entries(domainLayers).forEach(([domain, layers]) => {
      const visible = activeDomains.includes(domain) ? 'visible' : 'none';
      layers.forEach(lid => {
        if (map.current.getLayer(lid))
          map.current.setLayoutProperty(lid, 'visibility', visible);
      });
    });
  }, [activeDomains, loading]);

  // Update entity shading when reference perspective changes
  useEffect(() => {
    if (!map.current || loading) return;
    const source = map.current.getSource('entities');
    if (!source) return;
    axios.get(`${API_BASE}/api/v2/global/entities`)
      .then(r => {
        const entities = r.data.entities || [];
        const valid = entities.filter(e => (e.geojson || e.geography)?.geometry);
        source.setData({ type:'FeatureCollection',
          features: valid.map(e => {
            const refA = Object.values(referenceAssessments).find(a => a.source_entity_id === e.id);
            const displayLevel = refA ? refA.escalation_code : (referenceEntityId ? 'peace' : e.threat_level);
            return { type:'Feature', id:e.id,
              properties:{ name:e.name, threat_level:displayLevel },
              geometry: (e.geojson || e.geography).geometry };
          })
        });
      })
      .catch(() => {});
  }, [referenceAssessments, referenceEntityId, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (map.current) return;
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) { setError('Mapbox token not configured'); setLoading(false); return; }

    function bootstrap() {
      if (window.mapboxgl) { init(); return; }
      const link = document.createElement('link');
      link.href='https://cdn.jsdelivr.net/npm/mapbox-gl@3.3.0/dist/mapbox-gl.css'; link.rel='stylesheet';
      document.head.appendChild(link);
      const script = document.createElement('script');
      script.src='https://cdn.jsdelivr.net/npm/mapbox-gl@3.3.0/dist/mapbox-gl.js';
      script.onload=init;
      script.onerror=()=>{ setError('Failed to load map library'); setLoading(false); };
      document.head.appendChild(script);
    }

    function init() {
      if (!mapContainer.current || map.current) return;
      window.mapboxgl.accessToken = token;
      map.current = new window.mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/satellite-v9',
        center: [20,20], zoom: 2
      });
      map.current.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
      map.current.on('load', loadData);
    }

    async function loadData() {
      try {
        const [eR, cR] = await Promise.all([
          axios.get(`${API_BASE}/api/v2/global/entities`),
          axios.get(`${API_BASE}/api/v2/global/chokepoints`),
        ]);
        const entities = eR.data.entities || [];
        const chokes   = cR.data.chokepoints || [];

        // Country fills — no region outlines (we fly instead)
        const valid = entities.filter(e => (e.geojson || e.geography)?.geometry);
        addSourceSafe(map.current, 'entities', { type:'FeatureCollection',
          features: valid.map(e => {
            // In reference mode, use the assessment level from that entity TO the reference
            const refAssessment = Object.values(referenceAssessmentsRef.current).find(a => a.source_entity_id === e.id);
            const displayLevel = refAssessment ? refAssessment.escalation_code : (referenceEntityIdRef.current ? 'peace' : e.threat_level);
            return { type:'Feature', id:e.id,
              properties:{ name:e.name, threat_level:displayLevel, entity_id:e.id },
              geometry: (e.geojson || e.geography).geometry };
          }) });
        addLayerSafe(map.current, { id:'entities-fill', type:'fill', source:'entities',
          paint:{
            'fill-color':['match',['get','threat_level'],
              'peace','#1a1a2e',
              'economic_competition','#2d6a4f',
              'clandestine','#52b788',
              'covert','#ffcc33',
              'overt','#ff9933',
              'limited_conventional','#e63946',
              'lsco','#c1121f',
              'global_strategic','#7b2d8b',
              'nuclear_chemical','#3d0000',
              '#1a1a2e'],
            'fill-opacity':['match',['get','threat_level'],
              'peace',0.05,
              'economic_competition',0.25,
              'clandestine',0.30,
              'covert',0.40,
              'overt',0.50,
              'limited_conventional',0.55,
              'lsco',0.65,
              'global_strategic',0.75,
              'nuclear_chemical',0.85,
              0.05]
          }});
        addLayerSafe(map.current, { id:'entities-outline', type:'line', source:'entities',
          paint:{
            'line-color':['match',['get','threat_level'],
              'peace','rgba(255,255,255,0.08)',
              'economic_competition','rgba(45,106,79,0.7)',
              'clandestine','rgba(82,183,136,0.7)',
              'covert','rgba(255,204,51,0.8)',
              'overt','rgba(255,153,51,0.8)',
              'limited_conventional','rgba(230,57,70,0.9)',
              'lsco','rgba(193,18,31,0.9)',
              'global_strategic','rgba(123,45,139,0.9)',
              'nuclear_chemical','rgba(61,0,0,1.0)',
              'rgba(255,255,255,0.08)'],
            'line-width':1.2
          }});

        // D3 Maritime
        addSourceSafe(map.current, 'shipping', SHIPPING_LANES);
        addLayerSafe(map.current, { id:'d3-shipping', type:'line', source:'shipping',
          layout:{ visibility:'none' },
          paint:{ 'line-color':'#00bcd4', 'line-width':1.2, 'line-opacity':0.55, 'line-dasharray':[6,3] } });

        const chokeFeatures = chokes.filter(c => c.geojson)
          .map(c => ({ type:'Feature', properties:{ name:c.name }, geometry:c.geojson }));
        if (chokeFeatures.length > 0) {
          addSourceSafe(map.current, 'choke-lines', { type:'FeatureCollection', features:chokeFeatures });
          addLayerSafe(map.current, { id:'d3-choke-lines', type:'line', source:'choke-lines',
            layout:{ visibility:'none' },
            paint:{ 'line-color':'#ffff00', 'line-width':2, 'line-dasharray':[4,3] } });
        }
        addSourceSafe(map.current, 'choke-pts', CHOKEPOINTS);
        addLayerSafe(map.current, { id:'d3-choke-dots', type:'circle', source:'choke-pts',
          layout:{ visibility:'none' },
          paint:{ 'circle-radius':6, 'circle-color':'#ffff00', 'circle-stroke-color':'#000', 'circle-stroke-width':1.5 } });
        addLayerSafe(map.current, { id:'d3-choke-labels', type:'symbol', source:'choke-pts',
          layout:{ visibility:'none',
            'text-field':['concat',['get','name'],'\n',['get','detail']],
            'text-size':10, 'text-anchor':'top', 'text-offset':[0,0.9], 'text-max-width':10 },
          paint:{ 'text-color':'#ffff99', 'text-halo-color':'#000', 'text-halo-width':1.5 } });

        // Fetch real events from API and populate domain layers
        try {
          const evR = await axios.get(`${API_BASE}/api/v2/global/events?days=14`);
          const evList = evR.data.events || [];

          const toFeature = (e) => ({
            type:'Feature',
            properties:{
              label: e.title.length > 50 ? e.title.substring(0,50)+'…' : e.title,
              date: new Date(e.event_date).toLocaleDateString('en-US',{month:'short',day:'numeric'}),
              ci: Math.round((e.confidence_level||0.6)*100)
            },
            geometry:{ type:'Point', coordinates:[parseFloat(e.longitude), parseFloat(e.latitude)] }
          });

          const d1 = evList.filter(e => e.pmesii_category === 'Military');
          const d2 = evList.filter(e => e.pmesii_category === 'Military');
          const d6 = evList.filter(e => ['Information','Social','Political'].includes(e.pmesii_category));

          if (d1.length > 0) AIR_INCIDENTS.features   = d1.map(toFeature);
          if (d2.length > 0) LAND_INCIDENTS.features  = d2.map(toFeature);
          if (d6.length > 0) INFO_OPS.features         = d6.map(toFeature);

          console.log(`✅ Loaded ${evList.length} real events onto map`);
        } catch(evErr) {
          console.log('Using static event data:', evErr.message);
        }

        // D1 Air
        addSourceSafe(map.current, 'd1-src', { type:'FeatureCollection', features: AIR_INCIDENTS.features });
        addLayerSafe(map.current, { id:'d1-dots', type:'circle', source:'d1-src',
          layout:{ visibility:'none' },
          paint:{ 'circle-radius':7, 'circle-color':'#64b5f6', 'circle-stroke-color':'#000', 'circle-stroke-width':1.5 } });
        addLayerSafe(map.current, { id:'d1-labels', type:'symbol', source:'d1-src',
          layout:{ visibility:'none',
            'text-field':['concat',['get','label'],'\n',['get','date']],
            'text-size':10, 'text-anchor':'top', 'text-offset':[0,0.9], 'text-max-width':12 },
          paint:{ 'text-color':'#64b5f6', 'text-halo-color':'#000', 'text-halo-width':1.5 } });

        // D2 Land
        addSourceSafe(map.current, 'd2-src', { type:'FeatureCollection', features: LAND_INCIDENTS.features });
        addLayerSafe(map.current, { id:'d2-dots', type:'circle', source:'d2-src',
          layout:{ visibility:'none' },
          paint:{ 'circle-radius':7, 'circle-color':'#81c784', 'circle-stroke-color':'#000', 'circle-stroke-width':1.5 } });
        addLayerSafe(map.current, { id:'d2-labels', type:'symbol', source:'d2-src',
          layout:{ visibility:'none',
            'text-field':['concat',['get','label'],'\n',['get','date']],
            'text-size':10, 'text-anchor':'top', 'text-offset':[0,0.9], 'text-max-width':12 },
          paint:{ 'text-color':'#81c784', 'text-halo-color':'#000', 'text-halo-width':1.5 } });

        // D6 Information
        addSourceSafe(map.current, 'd6-src', { type:'FeatureCollection', features: INFO_OPS.features });
        addLayerSafe(map.current, { id:'d6-dots', type:'circle', source:'d6-src',
          layout:{ visibility:'none' },
          paint:{ 'circle-radius':7, 'circle-color':'#fff176', 'circle-stroke-color':'#000', 'circle-stroke-width':1.5 } });
        addLayerSafe(map.current, { id:'d6-labels', type:'symbol', source:'d6-src',
          layout:{ visibility:'none',
            'text-field':['concat',['get','label'],'\n',['get','date']],
            'text-size':10, 'text-anchor':'top', 'text-offset':[0,0.9], 'text-max-width':12 },
          paint:{ 'text-color':'#fff176', 'text-halo-color':'#000', 'text-halo-width':1.5 } });

        // Click handlers
        map.current.on('click', 'entities-fill', e => {
          if (e.features.length > 0)
            onRegionClick?.(e.features[0].id, e.features[0].properties.name);
        });
        map.current.on('mouseenter', 'entities-fill', () => { map.current.getCanvas().style.cursor = 'pointer'; });
        map.current.on('mouseleave', 'entities-fill', () => { map.current.getCanvas().style.cursor = ''; });

        ['d1-dots','d2-dots','d3-choke-dots','d6-dots'].forEach(lid => {
          map.current.on('click', lid, e => {
            if (!e.features.length) return;
            const p = e.features[0].properties;
            new window.mapboxgl.Popup({ closeButton:true })
              .setLngLat(e.lngLat)
              .setHTML(`<div style="font-size:12px;font-weight:bold;margin-bottom:3px">${p.name||p.label}</div><div style="font-size:11px;color:#aaa">${p.detail||p.date||''}</div>`)
              .addTo(map.current);
          });
          map.current.on('mouseenter', lid, () => { map.current.getCanvas().style.cursor = 'pointer'; });
          map.current.on('mouseleave', lid, () => { map.current.getCanvas().style.cursor = ''; });
        });

        setLoading(false);
      } catch(err) {
        setError('Failed to load map data: ' + err.message);
        setLoading(false);
      }
    }

    bootstrap();
    return () => { if (map.current) { map.current.remove(); map.current = null; } };
  }, [onRegionClick]);

  return (
    <div style={{ position:'relative', width:'100%', height:'clamp(340px, 55vw, 600px)' }}>
      {loading && (
        <div style={{ position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',zIndex:10,
          backgroundColor:'rgba(0,0,0,0.9)',padding:'20px 28px',borderRadius:'8px',color:'#c4a563',textAlign:'center' }}>
          ⏳ Loading operational environment…
        </div>
      )}
      {error && (
        <div style={{ position:'absolute',top:'10px',left:'10px',zIndex:10,
          backgroundColor:'rgba(160,20,20,0.9)',color:'white',padding:'10px 14px',borderRadius:'4px',fontSize:'12px' }}>
          ❌ {error}
        </div>
      )}
      <div ref={mapContainer} style={{ width:'100%', height:'100%' }} />
      {!loading && !error && <Legend activeDomains={activeDomains} />}
      <style>{`.mapboxgl-popup-content{background:#0f1419;color:#e8e9ed;border:1px solid rgba(196,165,99,0.3);border-radius:6px;padding:10px 12px;font-family:Georgia,serif;max-width:200px}.mapboxgl-popup-close-button{color:#888}`}</style>
    </div>
  );
});

GlobeMap.displayName = 'GlobeMap';
export default GlobeMap;
